/**
 * Default quote coefficients — a base set and one per order quantity. The same editor for the company defaults (admin
 * page) and for "my defaults" (My settings, JF 2026-10-16: colleagues could not find where to set them). Next to each
 * set: the GP the cost sheet open now would have with it.
 */
import { useEffect, useState } from "react";
import { DEFAULT_QCOEF, hasSheet, n, totals, type CoefPrefs, type QCoef } from "@ic/engine";
import { api } from "../../shared/api.ts";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { Field } from "../../shared/ui/common.tsx";
import { COEF_FIELDS } from "../cost-sheet/coef.ts";

const show = (v: number, kind: "pct" | "x") => (kind === "pct" ? String(+(v * 100).toFixed(4)) : String(v));
const read = (v: string, kind: "pct" | "x") => (kind === "pct" ? n(v) / 100 : n(v));

export function CoefEditor({ scope }: { scope: "company" | "mine" }) {
  const t = useT();
  const toast = useStore.getState().toast;
  const sheet = useStore((s) => s.sheet);
  const [base, setBase] = useState<QCoef>({ ...DEFAULT_QCOEF });
  const [rows, setRows] = useState<{ qty: number; c: QCoef }[]>([]);
  const [newQty, setNewQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [company, setCompany] = useState<CoefPrefs | null>(null);
  useEffect(() => {
    api.coefPrefs().then((r) => {
      setCompany(r.company ?? null);
      const own = scope === "company" ? r.company : r.mine;
      // mine starts from the company's when I have none
      setBase({ ...DEFAULT_QCOEF, ...(scope === "mine" ? r.company?.base || {} : {}), ...(own?.base || {}) });
      setRows(Object.entries(own?.byQty || {}).map(([q, c]) => ({ qty: Number(q), c: { ...DEFAULT_QCOEF, ...c } })).sort((a, b) => a.qty - b.qty));
      setLoaded(true);
    }).catch((e) => toast(String(e.message || e)));
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  function addQty() {
    const q = Math.round(n(newQty));
    if (!(q > 0)) return;
    if (rows.some((r) => r.qty === q)) { toast(t(`${q} pcs is already in the list`, `${q} 件已经在列表里`)); return; }
    setRows([...rows, { qty: q, c: { ...base } }].sort((a, b) => a.qty - b.qty)); setNewQty("");
  }
  async function save() {
    setBusy(true);
    try {
      const p: CoefPrefs = { base, byQty: Object.fromEntries(rows.map((r) => [String(r.qty), r.c])) };
      const st = useStore.getState();
      if (scope === "company") { await api.saveCompanyCoef(p); st.setCoefPrefs({ mine: st.coefPrefs?.mine ?? null, company: p }); }
      else { await api.saveMyCoef(p); st.setCoefPrefs({ mine: p, company: st.coefPrefs?.company ?? null }); }
      toast(scope === "company" ? t("Company defaults saved", "公司默认系数已保存") : t("Your defaults saved — new cost sheets use them", "已保存你的默认系数——新建的成本表都会用"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  async function clearMine() {
    if (!confirm(t("Remove your own defaults? New sheets then use the company defaults.", "删除你自己的默认系数？之后新建的单用公司默认。"))) return;
    setBusy(true);
    try {
      await api.saveMyCoef({});
      const st = useStore.getState(); st.setCoefPrefs({ mine: null, company: st.coefPrefs?.company ?? null });
      setBase({ ...DEFAULT_QCOEF, ...(company?.base || {}) }); setRows([]);
      toast(t("Back to the company defaults", "已改回公司默认"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  /** the GP the sheet open now would have with these coefficients (only the GP; the sheet does not change) */
  const gpWith = (c: QCoef) => {
    if (!hasSheet(sheet) || !sheet.qcoef) return "—";
    const tt = totals({ ...sheet, qcoef: c });
    return tt.qfob > 0 ? (tt.gpp * 100).toFixed(1) + "%" : "—"; // an empty sheet has no GP
  };
  const cells = (c: QCoef, on: (c: QCoef) => void, key: string) => COEF_FIELDS.map(([k, , , kind]) => (
    <td key={k}><Field className="in n" dataK={`${scope === "company" ? "cc" : "mc"}-${key}-${k}`} value={show(c[k] as number, kind)} onChange={(v) => on({ ...c, [k]: read(v, kind) })} /></td>
  ));
  if (!loaded) return <div className="dim">{t("Loading…", "加载中…")}</div>;
  const id = scope === "company" ? "cc" : "mc";
  return (
    <div id={scope === "company" ? "companyCoefEd" : "myCoef"}>
      <p className="dim" style={{ marginTop: 0, fontSize: 12.5 }}>{scope === "company" ? t(
        "Defaults for new sheets. For an order quantity the sheet uses: the person's own default for it → the company's for it → their own base → the company base below. A quantity not listed uses the largest listed quantity below it (else the smallest).",
        "新建成本表时的默认报价系数。某个数量用：本人该档默认 → 公司该档默认 → 本人基础默认 → 下面的公司基础默认。没列出的数量，用不超过它的最大一档（都比它大就用最小一档）。")
        : t("Your own defaults for NEW cost sheets (sheets already made keep theirs). A quantity not listed uses the largest listed quantity below it. With none of your own, the company defaults apply.",
          "你自己的默认报价系数，只用于新建的成本表（已经做好的单不变）。没列出的数量，用不超过它的最大一档。没有自己的默认时，用公司默认。")}</p>
      <div className="coefwrap"><table className="t coefgrid" id={id + "Table"}><tbody>
        <tr><th>{t("Order qty", "数量档")}</th>{COEF_FIELDS.map(([k, en, zh, kind]) => <th key={k}>{t(en, zh)}{kind === "pct" ? " %" : ""}</th>)}
          <th title={t("The GP the cost sheet open now would have with this set", "当前打开的成本表用这组系数时的 GP")}>{t("GP on the open sheet", "当前表 GP")}</th><th /></tr>
        <tr data-coefrow="base"><td><b>{t("Base (any qty)", "基础（不分数量）")}</b></td>{cells(base, setBase, "base")}<td className="mono" data-gp="base">{gpWith(base)}</td><td /></tr>
        {rows.map((r, i) => (
          <tr key={r.qty} data-coefrow={r.qty}><td className="mono">{r.qty} {t("pcs", "件")}</td>
            {cells(r.c, (c) => setRows(rows.map((x, j) => (j === i ? { ...x, c } : x))), String(r.qty))}
            <td className="mono" data-gp={r.qty}>{gpWith(r.c)}</td>
            <td><button className="btn sm" data-coefdel={r.qty} onClick={() => setRows(rows.filter((_, j) => j !== i))}>{t("Remove", "删除")}</button></td></tr>
        ))}
      </tbody></table></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input className="in" id={id + "NewQty"} style={{ maxWidth: 140 }} value={newQty} onChange={(e) => setNewQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addQty(); }} placeholder={t("e.g. 300", "如 300")} />
        <button className="btn sm" id={id + "Add"} onClick={addQty}>{t("+ Add quantity", "+ 加数量档")}</button>
        {scope === "company" ? <button className="btn sm" onClick={() => setBase({ ...DEFAULT_QCOEF })}>{t("Base = template values", "基础恢复模板值")}</button>
          : <button className="btn sm" id="mcClear" disabled={busy} onClick={clearMine}>{t("Use the company defaults", "改回公司默认")}</button>}
        <button className="btn sm p" id={id + "Save"} style={{ marginLeft: "auto" }} disabled={busy} onClick={save}>{busy ? t("Saving…", "保存中…") : t("Save", "保存")}</button>
      </div>
    </div>
  );
}
